import ChipRange from "@/components/ChipRange"
import ChipRangeInput from "@/components/ChipRangeInput"
import ChipSelect from "@/components/ChipSelect"
import { Overline } from "@/components/typography"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  ageBracketList,
  genderList,
  genreList,
  languageList,
} from "@/shared/grailConstants"
import { getCurrencySymbol } from "@/shared/currency"
import type {
  BrowseFilter,
  Currency,
  FilterRange,
  Platform,
} from "@/shared/types"
import { getClearedBrowseFilter } from "@/utils/browseFilter"
import { deepEqual } from "@/utils/core"
import { abbreviateNumber } from "@/utils/formatNumbers"

const DEFAULT_PCT_RANGE = { lower: 0, upper: 100 } as const

const EMPTY_RANGE: FilterRange = { lower: null, upper: null }

export type TalentFilterSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  value: BrowseFilter
  onChange: (value: BrowseFilter) => void
  platforms: Platform[]
  showPriceChips?: boolean
  title?: string
  description?: string
  currency?: Currency
  countryList?: string[]
  tagList?: string[]
  cityList?: string[]
  disabled?: boolean
}

export function TalentFilterSheet({
  open,
  onOpenChange,
  value,
  onChange,
  platforms,
  showPriceChips = false,
  title = "Filters",
  description,
  currency = "USD",
  countryList = [],
  tagList = [],
  cityList = [],
  disabled,
}: TalentFilterSheetProps) {
  const patch = (partial: Partial<BrowseFilter>) => {
    onChange({ ...value, ...partial })
  }

  const patchPlatformFilter = (platform: Platform, partial: { followersRange?: FilterRange; priceRange?: FilterRange }) => {
    patch({
      [platform]: {
        ...value[platform],
        ...partial,
      },
    })
  }

  const femaleRange = value.femaleFollowerPctRange ?? DEFAULT_PCT_RANGE
  const maleRange = value.maleFollowerPctRange ?? DEFAULT_PCT_RANGE
  const tagLogic = value.tagFilterLogic ?? "OR"
  const emailInBio = value.emailInBio ?? "Any"

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          {description ? (
            <SheetDescription>{description}</SheetDescription>
          ) : null}
        </SheetHeader>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4">
          <section>
            <Overline className="mb-3">Audience & content</Overline>
            {/* Chip row: wrap existing ChipSelect / ChipRange controls. */}
            <div className="flex flex-wrap gap-y-2">
              <ChipSelect
                label={
                  value.countries?.length
                    ? `Countries: ${value.countries.length}`
                    : "All Countries"
                }
                active={Boolean(value.countries?.length)}
                options={countryList}
                value={value.countries ?? []}
                onChange={(next) => patch({ countries: next as string[] })}
                disabled={disabled}
              />
              <ChipSelect
                label={
                  value.contentTags?.length
                    ? `Tags: ${value.contentTags.length}`
                    : "All Tags"
                }
                active={Boolean(value.contentTags?.length)}
                options={tagList}
                value={value.contentTags ?? []}
                onChange={(next) => patch({ contentTags: next as string[] })}
                disabled={disabled}
              />
              <ChipSelect
                label={
                  value.cities?.length
                    ? `City: ${value.cities.length}`
                    : "All Cities"
                }
                active={Boolean(value.cities?.length)}
                options={cityList}
                value={value.cities ?? []}
                onChange={(next) => patch({ cities: next as string[] })}
                disabled={disabled}
              />
              <ChipSelect
                label={
                  value.contentLanguage?.length
                    ? `Language: ${value.contentLanguage.length}`
                    : "All Languages"
                }
                active={Boolean(value.contentLanguage?.length)}
                options={[...languageList, "Other"]}
                value={value.contentLanguage ?? []}
                onChange={(next) =>
                  patch({ contentLanguage: next as string[] })
                }
                disabled={disabled}
              />
              <ChipSelect
                label={
                  value.gender?.length
                    ? `Creator Gender: ${value.gender.length}`
                    : "All Genders"
                }
                active={Boolean(value.gender?.length)}
                options={genderList}
                value={value.gender ?? []}
                onChange={(next) => patch({ gender: next as string[] })}
                disabled={disabled}
              />
              <ChipSelect
                label={
                  value.favoriteGenre?.length
                    ? `Favorite Genre: ${value.favoriteGenre.length}`
                    : "Favorite Genre"
                }
                active={Boolean(value.favoriteGenre?.length)}
                options={genreList}
                value={value.favoriteGenre ?? []}
                onChange={(next) =>
                  patch({ favoriteGenre: next as string[] })
                }
                disabled={disabled}
              />
              <ChipRange
                label={
                  deepEqual(femaleRange, DEFAULT_PCT_RANGE)
                    ? "Female Follower %"
                    : `Female Follower %: ${femaleRange.lower}-${femaleRange.upper}`
                }
                value={[femaleRange.lower, femaleRange.upper]}
                onChange={(next) => {
                  if (!next) return
                  patch({ femaleFollowerPctRange: next })
                }}
                min={DEFAULT_PCT_RANGE.lower}
                minLabel="0%"
                max={DEFAULT_PCT_RANGE.upper}
                maxLabel="100%"
                step={1}
                disabled={disabled}
              />
              <ChipRange
                label={
                  deepEqual(maleRange, DEFAULT_PCT_RANGE)
                    ? "Male Follower %"
                    : `Male Follower %: ${maleRange.lower}-${maleRange.upper}`
                }
                value={[maleRange.lower, maleRange.upper]}
                onChange={(next) => {
                  if (!next) return
                  patch({ maleFollowerPctRange: next })
                }}
                min={DEFAULT_PCT_RANGE.lower}
                minLabel="0%"
                max={DEFAULT_PCT_RANGE.upper}
                maxLabel="100%"
                step={1}
                disabled={disabled}
              />
              <ChipSelect
                label={
                  value.topFollowerCountries?.length
                    ? `Top Follower Countries: ${value.topFollowerCountries.length}`
                    : "Top Follower Country"
                }
                active={Boolean(value.topFollowerCountries?.length)}
                options={countryList}
                value={value.topFollowerCountries ?? []}
                onChange={(next) =>
                  patch({ topFollowerCountries: next as string[] })
                }
                disabled={disabled}
              />
              <ChipSelect
                label={
                  value.topFollowerAgeBrackets?.length
                    ? `Top Follower Age Bracket: ${value.topFollowerAgeBrackets.length}`
                    : "Top Follower Age Bracket"
                }
                active={Boolean(value.topFollowerAgeBrackets?.length)}
                options={ageBracketList}
                value={value.topFollowerAgeBrackets ?? []}
                onChange={(next) =>
                  patch({ topFollowerAgeBrackets: next as string[] })
                }
                disabled={disabled}
              />
              <ChipSelect
                label={
                  emailInBio === "Any"
                    ? "Email in Bio"
                    : `Email in Bio: ${emailInBio}`
                }
                active={emailInBio !== "Any"}
                options={["Yes", "No", "Any"]}
                singleSelect
                value={emailInBio}
                onChange={(next) =>
                  patch({
                    emailInBio: next as BrowseFilter["emailInBio"],
                  })
                }
                disabled={disabled}
              />
              <ChipSelect
                label={
                  tagLogic === "OR" ? "Match Any Tags" : "Match All Tags"
                }
                active={tagLogic !== "OR"}
                options={["OR", "AND"]}
                namesPrefix="Show Creators that match:"
                names={["Any Selected Tags", "All Selected Tags"]}
                value={tagLogic}
                onChange={(next) =>
                  patch({
                    tagFilterLogic: next as BrowseFilter["tagFilterLogic"],
                  })
                }
                singleSelect
                disabled={disabled}
              />
            </div>
          </section>

          {platforms.map((platform) => {
            const followers = value[platform]?.followersRange ?? EMPTY_RANGE
            const price = value[platform]?.priceRange ?? EMPTY_RANGE

            return (
              <section key={platform}>
                <Overline className="mb-3">{platform}</Overline>
                {/* Per-platform range chips — followers always, price by surface. */}
                <div className="flex flex-wrap gap-y-2">
                  <ChipRangeInput
                    label={followerChipLabel(followers, platform)}
                    value={[followers.lower, followers.upper]}
                    onChange={(next) =>
                      patchPlatformFilter(platform, {
                        followersRange: { lower: next.lower, upper: next.upper },
                      })
                    }
                    min={0}
                    minPlaceholder="Min"
                    maxPlaceholder="Max"
                    disabled={disabled}
                  />
                  {showPriceChips ? (
                    <ChipRangeInput
                      label={priceChipLabel(price, currency)}
                      value={[price.lower, price.upper]}
                      onChange={(next) =>
                        patchPlatformFilter(platform, {
                          priceRange: { lower: next.lower, upper: next.upper },
                        })
                      }
                      min={0}
                      startAddon={getCurrencySymbol(currency)}
                      minPlaceholder="Min"
                      maxPlaceholder="Max"
                      disabled={disabled}
                    />
                  ) : null}
                </div>
              </section>
            )
          })}
        </div>

        {/* Footer actions sit in one row opposite the sheet close. */}
        <SheetFooter className="flex-row justify-end">
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            onClick={() => onChange(getClearedBrowseFilter(value))}
          >
            Clear all
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

function rangeChipLabel(
  noun: string,
  range: FilterRange | undefined,
  format: (n: number) => string
) {
  const lower = range?.lower ?? null
  const upper = range?.upper ?? null
  if (lower == null && upper == null) return `Any ${noun}`
  if (lower != null && upper != null) {
    return `${noun}: ${format(lower)}-${format(upper)}`
  }
  if (lower != null) return `${noun}: ${format(lower)}+`
  return `${noun}: up to ${format(upper!)}`
}

function followerChipLabel(
  range: FilterRange | undefined,
  platform: Platform
) {
  const noun = platform === "YouTube" ? "Subscribers" : "Followers"
  return rangeChipLabel(noun, range, (n) => String(abbreviateNumber(n)))
}

function priceChipLabel(
  range: FilterRange | undefined,
  currency: Currency
) {
  const symbol = getCurrencySymbol(currency)
  return rangeChipLabel("Price", range, (n) => `${symbol}${abbreviateNumber(n)}`)
}
